import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { STATUS, BOOKING_STEP } from '~/literals';

import { ErrorResponse, FlightBookingState, FlightReservation, PaymentRequest, ReservationEmailParams } from '~/types';
import { FlightOfferPriceCheckResponse } from '~/types/flights';

export const flightBookingState: FlightBookingState = {
  offerBookingDetails: null,
  alertMessage: '',
  errorMessage: '',
  pageStatus: STATUS.IDLE,
  bookingStep: BOOKING_STEP.FLIGHT_SEARCH,
  flightPostBookingParameters: null,
  paymentRequest: null,
  paymentBookingId: '',
  reservation: null,
  userReservations: [],
  receivedFromSearchResultsPage: false,
  emailSendStatus: STATUS.IDLE,
};

export interface GetFlightsBookingQuery {
  id: string;
}

export const flightBookingSlice = createSlice({
  name: 'flightBooking',
  initialState: flightBookingState,
  reducers: {
    getFlightOfferBookingDetails: (draft, _: PayloadAction<GetFlightsBookingQuery>) => {
      draft.alertMessage = '';
      draft.pageStatus = STATUS.RUNNING;
      draft.receivedFromSearchResultsPage = false;
    },
    getFlightOfferBookingDetailsSuccess: (draft, { payload }: PayloadAction<FlightOfferPriceCheckResponse>) => {
      draft.bookingStep = BOOKING_STEP.PASSENGER_DETAILS_CAPTURING;
      draft.offerBookingDetails = payload;
      draft.pageStatus = STATUS.SUCCESS;
    },
    getFlightOfferBookingDetailsFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.offerBookingDetails = null;
      draft.errorMessage = payload.errorMessage;
      draft.pageStatus = STATUS.ERROR;
    },
    getReservation: (draft, { payload }: PayloadAction<string>) => {
      draft.paymentBookingId = payload;
      draft.pageStatus = STATUS.RUNNING;
      draft.paymentRequest = null;
    },
    getReservationSuccess: (draft, { payload }: PayloadAction<FlightReservation>) => {
      draft.bookingStep = BOOKING_STEP.FLIGHT_SEARCH;
      draft.reservation = payload;
      draft.pageStatus = STATUS.SUCCESS;
      draft.offerBookingDetails = null;
    },
    getReservationFailed: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.errorMessage = payload.errorMessage;
      draft.pageStatus = STATUS.ERROR;
    },
    getUserReservations: (draft) => {
      draft.pageStatus = STATUS.RUNNING;
    },
    getUserReservationsSuccess: (draft, { payload }: PayloadAction<FlightReservation[]>) => {
      draft.userReservations = payload;
      draft.pageStatus = STATUS.SUCCESS;
    },
    getUserReservationsFailed: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.errorMessage = payload.errorMessage;
      draft.pageStatus = STATUS.ERROR;
    },
    setLoginRequired: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.errorMessage = payload.errorMessage;
      draft.pageStatus = STATUS.LOGIN_REQUIRED;
    },
    requestPayment: (draft, _: PayloadAction<FlightBookingState>) => {
      draft.pageStatus = STATUS.RUNNING;
    },
    requestPaymentSuccess: (draft, { payload }: PayloadAction<PaymentRequest>) => {
      draft.bookingStep = BOOKING_STEP.PAYMENT_REQUESTED;
      draft.paymentRequest = payload;
      draft.pageStatus = STATUS.SUCCESS;
      draft.errorMessage = '';
    },
    requestPaymentFail: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.errorMessage = payload.errorMessage;
      draft.bookingStep = BOOKING_STEP.PAYMENT_REQUESTED;
      draft.paymentRequest = null;
      draft.pageStatus = STATUS.ERROR;
    },
    sendReservationEmailReset: (draft) => {
      draft.emailSendStatus = STATUS.IDLE;
    },
    sendReservationEmail: (draft, _: PayloadAction<ReservationEmailParams>) => {
      draft.emailSendStatus = STATUS.RUNNING;
    },
    sendReservationEmailReservationSuccess: (draft) => {
      draft.emailSendStatus = STATUS.SUCCESS;
    },
    sendReservationEmailReservationFailed: (draft) => {
      draft.emailSendStatus = STATUS.ERROR;
    },
    setPriceCheckFromSearchResult: (draft) => {
      draft.receivedFromSearchResultsPage = true;
    },
    setBookingStepToPassengerDetailsCapturing: (draft) => {
      draft.bookingStep = BOOKING_STEP.PASSENGER_DETAILS_CAPTURING;
    },
    setBookingStepToAddBaggage: (draft) => {
      draft.bookingStep = BOOKING_STEP.BAGGAGE_ADDITION;
    },
    setBookingStepToPaymentRequested: (draft) => {
      draft.bookingStep = BOOKING_STEP.PAYMENT_REQUESTED;
    },
    setBookingStepToPaymentConfirmation: (draft) => {
      draft.bookingStep = BOOKING_STEP.PAYMENT_CONFIRMATION;
    },
    setBookingStepToPaymentSuccess: (draft) => {
      draft.bookingStep = BOOKING_STEP.PAYMENT_SUCCESS;
    },
    setBookingStepToPaymentFailed: (draft) => {
      draft.bookingStep = BOOKING_STEP.PAYMENT_FAILURE;
    },
    setBookingStepToBookingComplete: (draft) => {
      draft.bookingStep = BOOKING_STEP.PAYMENT_FAILURE;
    },
    resetBookingData: (draft) => {
      draft.offerBookingDetails = null;
      draft.alertMessage = '';
      draft.errorMessage = '';
      draft.pageStatus = STATUS.IDLE;
      draft.bookingStep = BOOKING_STEP.FLIGHT_SEARCH;
      draft.flightPostBookingParameters = null;
      draft.paymentRequest = null;
      draft.paymentBookingId = '';
      draft.reservation = null;
      draft.receivedFromSearchResultsPage = false;
    },
  },
});

export const {
  getFlightOfferBookingDetails,
  getFlightOfferBookingDetailsSuccess,
  getFlightOfferBookingDetailsFailure,
  setBookingStepToPassengerDetailsCapturing,
  setBookingStepToAddBaggage,
  setBookingStepToPaymentRequested,
  setBookingStepToPaymentSuccess,
  setBookingStepToPaymentFailed,
  setBookingStepToBookingComplete,
  requestPayment,
  requestPaymentSuccess,
  requestPaymentFail,
  getReservation,
  getReservationSuccess,
  getReservationFailed,
  setBookingStepToPaymentConfirmation,
  setPriceCheckFromSearchResult,
  resetBookingData,
  getUserReservations,
  getUserReservationsSuccess,
  getUserReservationsFailed,
  setLoginRequired,
  sendReservationEmail,
  sendReservationEmailReservationSuccess,
  sendReservationEmailReservationFailed,
  sendReservationEmailReset,
} = flightBookingSlice.actions;

export default flightBookingSlice.reducer;
